import queryString from 'query-string';

import { ROLE_PREVIEW_OVERRIDE } from '../../constants/localStorage';
import { ROLES, type Role } from './roles';

/**
 * The URL param this axis owns, end to end.
 *
 * Exported so `src/utils/previewOverrideUrl.ts` can consume and strip it without
 * re-declaring the name. The promote/strip side effect lives there rather than here
 * because it needs the app's `history` singleton, which cannot be imported into this
 * module: `createBrowserHistory()` runs at module scope and throws without a DOM, and
 * `rolePreviewOverride.test.ts` exercises the pure resolver below in the node project.
 */
export const ROLE_PREVIEW_PARAM = 'preview_role';

/**
 * Every role the preview may target — deliberately NOT `Object.values(ROLES)`.
 *
 * `support` is excluded: the preview only ever DOWNGRADES. It exists to answer "what
 * does an agency / member / read-only seat see?", and a value that grants more than the
 * real seat would turn a shareable link into an escalation primitive. The reader is
 * gated on a real support role as well (`useIsRealSupport` in `./useRole`); this is the
 * second layer, so that neither one alone is load-bearing.
 *
 * The Account menu's SEAT_OPTIONS already omits `support`, so no call site changes.
 */
const PREVIEWABLE_ROLES: ReadonlyArray<string> = [ROLES.ADMIN, ROLES.MEMBER, ROLES.READONLY];

function readRole(raw: unknown): Role | null {
  return typeof raw === 'string' && PREVIEWABLE_ROLES.includes(raw) ? (raw as Role) : null;
}

/**
 * Support-only debug override that renders the app as a different seat.
 *
 * **This is presentation only.** The API still enforces the real role carried by the
 * access token, so a previewed agency seat cannot actually perform anything the real
 * account may not. It exists to answer "what does an agency admin see?", not to grant
 * anything — which matters here because a Nexoya support account bypasses plan gating,
 * making it the one seat that can never see the Nexoya Starter experience truthfully.
 *
 * Returns `null` for "no override" — deliberately not a default role, which would
 * silently mask the real one while looking like it worked.
 *
 * Mirrors `resolveStarterPlanOverride` in `src/entitlements/`. Kept as a separate module
 * rather than shared with it on purpose: each axis owns its own preview, which is the
 * same separation that keeps `PERMISSIONS` and `FEATURES` independent.
 */
export function resolveRolePreviewOverride({
  search,
  stored,
}: {
  search?: string | null;
  stored?: string | null;
}): Role | null {
  const fromUrl = search ? readRole(queryString.parse(search)[ROLE_PREVIEW_PARAM]) : null;
  return fromUrl ?? readRole(stored);
}

export function getRolePreviewOverride(): Role | null {
  return resolveRolePreviewOverride({
    search: window.location.search,
    // The ACCESS throws in a locked or private-mode profile, and this runs in the mount
    // initialiser of every `useCan` consumer — unguarded it takes the whole app down.
    stored: readStored(ROLE_PREVIEW_OVERRIDE),
  });
}

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function setRolePreviewOverride(role: Role | null): void {
  try {
    if (role === null) {
      window.localStorage.removeItem(ROLE_PREVIEW_OVERRIDE);
    } else {
      window.localStorage.setItem(ROLE_PREVIEW_OVERRIDE, role);
    }
  } catch {
    // A blocked store costs the preview its persistence, nothing else.
  }
  // Same-tab listeners: the native `storage` event only fires in *other* tabs.
  window.dispatchEvent(new CustomEvent(ROLE_PREVIEW_OVERRIDE_EVENT));
}

export const ROLE_PREVIEW_OVERRIDE_EVENT = 'nexoya:role-preview-override';
